import React, { useState, useEffect } from 'react';
import { useArticles } from '../hooks/useArticles';
import { useCategories } from '../hooks/useCategories';
import HeroSection from '../components/home/HeroSection';
import ArticleGrid from '../components/home/ArticleGrid';
import FeaturedArticle from '../components/home/FeaturedArticle';
import Sidebar from '../components/home/Sidebar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import btv from '../images/btv.png';

const HomePage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('');
  const articlesPerPage = 10;

  const {
    data: articlesData,
    isLoading: articlesLoading,
    error: articlesError
  } = useArticles(currentPage, articlesPerPage, selectedCategory);

  const {
    data: categories,
    isLoading: categoriesLoading
  } = useCategories();

  const {
    data: featuredData,
    isLoading: featuredLoading
  } = useArticles(1, '', true);

  const {
    data: popularData,
    isLoading: popularLoading
  } = useArticles(1, 5, '', false, 'popular');

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (categorySlug) => {
    setSelectedCategory(categorySlug);
    setCurrentPage(1);
  };

  if (articlesLoading && currentPage === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (articlesError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Erreur de chargement
          </h2>
          <p className="text-gray-600 mb-4">
            Impossible de charger les articles. Veuillez réessayer.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Recharger la page
          </button>
        </div>
      </div>
    );
  }

  const articles = articlesData?.articles || [];
  const totalPages = articlesData?.totalPages || 1;
  const featuredArticles = featuredData?.articles || [];
  const popularArticles = popularData?.articles || [];

  const mainFeaturedArticle = featuredArticles[0];

  const filteredArticles = articles.filter(article =>
    !mainFeaturedArticle || article.id !== mainFeaturedArticle.id
  );

  const secondaryFeaturedArticles = featuredArticles.slice(1, 3).filter(article =>
    !mainFeaturedArticle || article.id !== mainFeaturedArticle.id
  );

  return (
    <div className="min-h-screen bg-gray-50 mt-4">
     

      {!featuredLoading && mainFeaturedArticle && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <HeroSection article={mainFeaturedArticle} />
            </div>

            <div className="lg:col-span-1 mt-40">
              <div className="bg-white rounded-lg shadow overflow-hidden h-full">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Regardez nos vidéos
                  </h3>
                  <p className="text-sm text-gray-600">
                    Découvrez notre chaîne YouTube
                  </p>
                </div>
                <a
                  href="https://www.youtube.com/@user-ij6tn9lu5b"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="relative pb-[56.25%]">
                    <img
                      src={btv}
                      alt="Regardez sur YouTube"
                      className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 hover:bg-opacity-50 transition-all duration-300">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-16 w-16 text-white drop-shadow-lg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        aria-label="Lire la vidéo"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </a>
                <div className="p-4">
                  <p className="text-sm text-gray-600">
                    Cliquez pour visiter notre chaîne YouTube et découvrir nos dernières vidéos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!categoriesLoading && categories && (
        <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8 overflow-x-auto py-4">
              <button
                onClick={() => handleCategoryChange('')}
                className={`whitespace-nowrap px-3 py-2 text-sm font-medium rounded-md ${selectedCategory === ''
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Toutes
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.slug)}
                  className={`whitespace-nowrap px-3 py-2 text-sm font-medium rounded-md ${selectedCategory === category.slug
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

          <div className="lg:col-span-3">
            {!featuredLoading && secondaryFeaturedArticles.length > 0 && (
              <div className="mb-8">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {secondaryFeaturedArticles.map((article) => (
                    <FeaturedArticle key={article.id} article={article} />
                  ))}
                </div>
              </div>
            )}

            {/* Titre de section */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedCategory ? `Catégorie: ${selectedCategory}` : ''}
              </h2>
              {selectedCategory && (
                <button
                  onClick={() => handleCategoryChange('')}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Voir tout
                </button>
              )}
            </div>

            {/* Grille d'articles (sans duplication) */}
            {articlesLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <ArticleGrid articles={filteredArticles} />
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center space-x-4 mt-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex items-center px-4 py-2 rounded-md ${currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-1" />
                  Précédent
                </button>

                <div className="flex space-x-2">
                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 rounded-md ${currentPage === page
                            ? 'bg-blue-800 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`flex items-center px-4 py-2 rounded-md ${currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                >
                  Suivant
                  <ChevronRightIcon className="h-5 w-5 ml-1" />
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Sidebar
              popularArticles={popularArticles.filter(article =>
                !mainFeaturedArticle || article.id !== mainFeaturedArticle.id
              )}
              categories={categories}
              isLoading={popularLoading || categoriesLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;




